import { useEffect, useState } from 'react';
import Routes from '../configuration/routes/RouteList';
import { Link, useNavigate } from 'react-router-dom';
import signIn from '../api/actions/signIn';
import loadAuth from '../api/loader/loadAuth';
import getApiUrl from '../configuration/getApiUrl';
import {
  getEnabledSupabaseProviders,
  getSupabaseAccessToken,
  isSupabaseAuthConfigured,
  signInWithSupabase,
  signInWithSupabaseProvider,
} from '../lib/supabaseAuth';

const Login = () => {
  const navigate = useNavigate();
  const useSupabaseAuth = isSupabaseAuthConfigured();
  const enabledProviders = getEnabledSupabaseProviders();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [saveLogin, setSaveLogin] = useState(false);
  const [waitingForBackend, setWaitingForBackend] = useState(false);
  const [waitedCount, setWaitedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSocialSignIn = async (provider: 'google' | 'apple') => {
    setErrorMessage(null);
    const redirectTo = `${window.location.origin}${Routes.AuthCallback}`;
    const { error } = await signInWithSupabaseProvider(provider, redirectTo);
    if (error) {
      setErrorMessage(error.message || `Failed to start ${provider} sign-in.`);
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    if (waitingForBackend) return false;

    setErrorMessage(null);

    if (useSupabaseAuth) {
      const { error } = await signInWithSupabase(username, password);
      if (!error) {
        navigate(Routes.Home);
        return true;
      }

      setErrorMessage(error.message || 'Unknown Error');
      navigate(Routes.Login);
      return false;
    }

    const loginResponse = await signIn(username, password, saveLogin);
    const signedIn = loginResponse.status === 204;

    if (signedIn) {
      navigate(Routes.Home);
    } else {
      const data = await loginResponse.json();
      setErrorMessage(data?.error || 'Unknown Error');
      navigate(Routes.Login);
    }
  };

  useEffect(() => {
    let retryCount = 0;
    let cancelled = false;

    const checkSupabaseSession = async () => {
      if (!useSupabaseAuth) {
        return;
      }

      try {
        const accessToken = await getSupabaseAccessToken();
        if (!accessToken) return;

        const auth = await loadAuth();
        if (auth.ok && !cancelled) {
          navigate(Routes.Home);
        }
      } catch (error) {
        console.log('Checking existing Supabase session: ', error);
      }
    };

    const checkBackendHealth = async () => {
      try {
        const health = await fetch(`${getApiUrl()}/api/health/`, {
          credentials: 'omit',
          cache: 'no-store',
        });

        if (health.ok) {
          setWaitingForBackend(false);
          return true;
        }
      } catch (error) {
        console.log('Checking backend availability: ', error);
      }

      retryCount += 1;
      setWaitedCount(retryCount);
      setWaitingForBackend(true);
      return false;
    };

    checkSupabaseSession();

    const backendCheckInterval = setInterval(async () => {
      if (useSupabaseAuth) {
        await checkBackendHealth();
        return;
      }

      try {
        const auth = await loadAuth();
        const authData = await auth.json();

        if (auth.status === 403) {
          setWaitingForBackend(false);
          clearInterval(backendCheckInterval);
        }

        if (authData.response === 'pong') {
          setWaitingForBackend(false);
          clearInterval(backendCheckInterval);
          navigate(Routes.Home);
        }
      } catch (error) {
        console.log('Checking backend availability: ', error);
        retryCount += 1;
        setWaitedCount(retryCount);
        setWaitingForBackend(true);
      }
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(backendCheckInterval);
    };
  }, [navigate, useSupabaseAuth]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'oklch(0.13 0.008 260)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <title>NM | Welcome</title>

      {/* Background glow */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '600px',
        height: '400px',
        background: 'radial-gradient(ellipse, oklch(0.52 0.22 265 / 0.12) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
        <span style={{
          fontWeight: 800,
          fontSize: '18px',
          letterSpacing: '-0.04em',
          background: 'linear-gradient(135deg, oklch(0.78 0.18 260), oklch(0.65 0.22 300))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>NM</span>
        <span style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.02em', color: 'oklch(0.9 0.004 260)' }}>
          <strong>Niu</strong><span style={{ opacity: 0.55, fontStyle: 'italic' }}>Media</span>
        </span>
      </div>

      {/* Card */}
      <div style={{
        width: '100%',
        maxWidth: '380px',
        borderRadius: '16px',
        border: '1px solid oklch(1 0 0 / 0.1)',
        background: 'oklch(0.17 0.008 260)',
        padding: '36px 32px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 32px 64px oklch(0 0 0 / 0.4)',
      }}>
        {/* Top edge gradient */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, oklch(0.52 0.22 265 / 0.5), transparent)',
        }} />

        <h1 style={{
          fontSize: '20px',
          fontWeight: 800,
          letterSpacing: '-0.03em',
          color: 'oklch(0.97 0.004 260)',
          margin: '0 0 6px',
        }}>
          Welcome back
        </h1>
        <p style={{
          fontSize: '13px',
          color: 'oklch(0.55 0.01 260)',
          margin: '0 0 28px',
        }}>
          {useSupabaseAuth ? 'Sign in with your email' : 'Sign in to your NiuMedia server'}
        </p>

        {/* Error */}
        {errorMessage !== null && (
          <div style={{
            marginBottom: '20px',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            border: '1px solid oklch(0.6 0.2 25)',
            color: 'oklch(0.75 0.18 25)',
            background: 'oklch(0.6 0.2 25 / 0.08)',
          }}>
            Failed to sign in — {errorMessage}
          </div>
        )}

        {useSupabaseAuth && enabledProviders.length > 0 && (
          <>
            {enabledProviders.map(provider => (
              <button
                key={provider}
                type="button"
                onClick={() => handleSocialSignIn(provider)}
                style={{
                  width: '100%',
                  padding: '11px',
                  borderRadius: '8px',
                  border: '1px solid oklch(1 0 0 / 0.12)',
                  background: 'oklch(0.2 0.008 260)',
                  color: 'oklch(0.95 0.004 260)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '14px',
                  fontFamily: 'inherit',
                }}
              >
                Continue with {provider === 'apple' ? 'Apple' : 'Google'}
              </button>
            ))}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
                color: 'oklch(0.45 0.01 260)',
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'oklch(1 0 0 / 0.08)' }} />
              <span>or use email</span>
              <div style={{ flex: 1, height: '1px', background: 'oklch(1 0 0 / 0.08)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'oklch(0.5 0.01 260)', marginBottom: '6px' }}>
              {useSupabaseAuth ? 'Email' : 'Username'}
            </label>
            <input
              type={useSupabaseAuth ? 'email' : 'text'}
              name="username"
              id="id_username"
              placeholder={useSupabaseAuth ? 'you@example.com' : 'your-username'}
              autoComplete={useSupabaseAuth ? 'email' : 'username'}
              maxLength={150}
              required
              value={username}
              onChange={event => setUsername(event.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid oklch(1 0 0 / 0.1)',
                background: 'oklch(0.22 0.008 260)',
                color: 'oklch(0.95 0.004 260)',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'oklch(0.5 0.01 260)', marginBottom: '6px' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              id="id_password"
              placeholder="••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={event => setPassword(event.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid oklch(1 0 0 / 0.1)',
                background: 'oklch(0.22 0.008 260)',
                color: 'oklch(0.95 0.004 260)',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {!useSupabaseAuth && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                name="remember_me"
                id="id_remember_me"
                checked={saveLogin}
                onChange={() => setSaveLogin(!saveLogin)}
                style={{ accentColor: 'oklch(0.52 0.22 265)', width: '14px', height: '14px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '12px', color: 'oklch(0.55 0.01 260)' }}>Remember me</span>
            </label>
          )}

          <input type="hidden" name="next" value={Routes.Home} />

          {waitingForBackend ? (
            <div style={{
              padding: '11px',
              borderRadius: '8px',
              background: 'oklch(0.52 0.22 265 / 0.1)',
              border: '1px solid oklch(0.52 0.22 265 / 0.2)',
              fontSize: '13px',
              color: 'oklch(0.72 0.18 265)',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: '2px solid oklch(0.52 0.22 265 / 0.3)',
                borderTopColor: 'oklch(0.72 0.18 265)',
                animation: 'spin 0.8s linear infinite',
              }} />
              Connecting to backend…
            </div>
          ) : (
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '11px',
                borderRadius: '8px',
                border: 'none',
                background: 'oklch(0.52 0.22 265)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 16px oklch(0.52 0.22 265 / 0.35)',
                fontFamily: 'inherit',
                letterSpacing: '-0.01em',
              }}
            >
              Sign in
            </button>
          )}
        </form>
      </div>

      {/* Backend trouble help */}
      {waitedCount > 10 && (
        <div style={{
          marginTop: '20px',
          width: '100%',
          maxWidth: '380px',
          padding: '20px 24px',
          borderRadius: '12px',
          border: '1px solid oklch(0.75 0.16 80 / 0.25)',
          background: 'oklch(0.75 0.16 80 / 0.06)',
        }}>
          <p style={{ fontSize: '13px', fontWeight: 600, color: 'oklch(0.82 0.14 80)', margin: '0 0 10px' }}>
            Having trouble connecting?
          </p>
          <p style={{ fontSize: '12px', color: 'oklch(0.6 0.01 260)', margin: '0 0 10px', lineHeight: 1.6 }}>
            Verify your environment variables:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[
              `TA_HOST=${window.location.origin}`,
              'REDIS_CON=redis://archivist-redis:6379',
            ].map(line => (
              <div
                key={line}
                onClick={() => navigator.clipboard.writeText(line)}
                title="Click to copy"
                style={{
                  padding: '7px 10px',
                  borderRadius: '6px',
                  background: 'oklch(0.22 0.008 260)',
                  border: '1px solid oklch(1 0 0 / 0.08)',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  color: 'oklch(0.72 0.18 265)',
                  cursor: 'copy',
                  letterSpacing: '0.01em',
                }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ marginTop: '20px', fontSize: '13px', color: 'oklch(0.45 0.01 260)' }}>
        Don't have an account?{' '}
        <Link
          to={Routes.Register}
          style={{ color: 'oklch(0.72 0.18 265)', textDecoration: 'none', fontWeight: 500 }}
        >
          Create one
        </Link>
      </p>

      <p
        style={{
          marginTop: '12px',
          fontSize: '11px',
          color: 'oklch(0.38 0.01 260)',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Link to={Routes.Privacy} style={{ color: 'inherit', textDecoration: 'none' }}>
          Privacy Policy
        </Link>
        <Link to={Routes.Terms} style={{ color: 'inherit', textDecoration: 'none' }}>
          Terms of Service
        </Link>
      </p>

      <p style={{ marginTop: '12px', fontSize: '11px', color: 'oklch(0.35 0.008 260)' }}>
        NiuMedia · Self-hosted media server
      </p>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        #id_username:focus, #id_password:focus {
          border-color: oklch(0.52 0.22 265 / 0.5) !important;
          box-shadow: 0 0 0 3px oklch(0.52 0.22 265 / 0.12);
        }
      `}</style>
    </div>
  );
};

export default Login;
